import React, { useState, useEffect } from 'react';
import GlobalClientCharts from './GlobalClientCharts';
import ClientPerformanceOverview from './ClientPerformanceOverview';

const DashboardSection = ({ clients }) => {
  const [filteredClients, setFilteredClients] = useState(clients);
  const [filters, setFilters] = useState({
    specialty: '',
    cell: '',
    country: ''
  });

  // Listas de opciones para los filtros
  const allSpecialties = [
    '', 'Medicina General', 'Cardiología', 'Dermatología', 'Endocrinología', 'Gastroenterología',
    'Ginecología y Obstetricia', 'Neurología', 'Oncología', 'Oftalmología', 'Ortopedia y Traumatología',
    'Otorrinolaringología', 'Pediatría', 'Psiquiatría', 'Psicología', 'Reumatología',
    'Urología', 'Neumología', 'Cirugía General', 'Cirugía Laparoscópica', 'Cirugía Plástica y Reconstructiva',
    'Cirugía Cardiovascular', 'Cirugía Pediátrica', 'Cirugía Oncológica', 'Cirugía Coloproctológica',
    'Cirugía Maxilofacial', 'Medicina Interna', 'Nefrología', 'Hematología', 'Medicina Física y Rehabilitación',
    'Anestesiología', 'Medicina Familiar', 'Infectología', 'Radiología', 'Endodoncia (odontología especializada)',
    'Odontología (Dentista)', 'Nutriología', 'Terapia Física', 'Medicina del Deporte', 'Toxicología',
    'Genética Médica', 'Fisioterapia', 'Terapia Ocupacional', 'Logopedia (Terapia del lenguaje)'
  ];

  const allCells = [
    '', 'C1GA', 'C2GA', 'C3GA', 'C4GA', 'C5GA', 'C6GA',
    'C1MA', 'C2MA', 'C3MA', 'C4MA',
    'G1M1', 'G1M2', 'G1M3', 'G1M4',
    'G2M1', 'G2M2', 'G2M3', 'G2M4',
    'G3M1', 'G3M2', 'G3M3', 'G3M4',
    'G4M1', 'G4M2', 'G4M3', 'G4M4',
    'G5M1', 'G5M2', 'G5M3', 'G5M4',
    'G6M1', 'G6M2', 'G6M3', 'G6M4'
  ];

  const allCountries = [
    '', 'México', 'Estados Unidos', 'Canadá', 'España', 'Colombia', 'Perú', 'Ecuador', 'Panamá',
    'República Dominicana', 'El Salvador', 'Chile', 'Honduras', 'Guatemala', 'Belice', 'Paraguay',
    'Uruguay', 'Bolivia'
  ];

  useEffect(() => {
    applyFilters();
  }, [clients, filters]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    let currentFilteredClients = [...clients];

    if (filters.specialty) {
      currentFilteredClients = currentFilteredClients.filter(client => 
        client.specialty === filters.specialty
      );
    }
    if (filters.cell) {
      currentFilteredClients = currentFilteredClients.filter(client => 
        client.cell === filters.cell
      );
    }
    if (filters.country) {
      currentFilteredClients = currentFilteredClients.filter(client => 
        client.country === filters.country
      );
    }
    setFilteredClients(currentFilteredClients);
  };

  const handleClearFilters = () => {
    setFilters({ specialty: '', cell: '', country: '' });
  };

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-[#0061A8]">Dashboard de Clientes</h2>
        <div className="text-sm text-[#63666A]">
          {filteredClients.length} clientes mostrados de {clients.length} total
        </div>
      </div>

      {/* Tarjetas de Resumen */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-[#0061A8]">
          <h4 className="text-sm font-medium text-[#63666A] mb-2">Total Clientes</h4>
          <p className="text-2xl font-bold text-[#0061A8]">{clients.length}</p>
          <p className="text-xs text-[#63666A] mt-1">
            {clients.filter(c => c.meetings.length > 0).length} clientes activos
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-[#A8D08D]">
          <h4 className="text-sm font-medium text-[#63666A] mb-2">Total Juntas</h4>
          <p className="text-2xl font-bold text-[#0061A8]">
            {clients.reduce((sum, client) => sum + client.meetings.length, 0)}
          </p>
          <p className="text-xs text-[#63666A] mt-1">
            {clients.filter(c => c.meetings.some(m => new Date(m.date) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000))).length} clientes con juntas este mes
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-[#63666A]">
          <h4 className="text-sm font-medium text-[#63666A] mb-2">Total Contactos</h4>
          <p className="text-2xl font-bold text-[#0061A8]">
            {clients.reduce((sum, client) => 
              sum + client.meetings.reduce((mSum, meeting) => mSum + meeting.contactsGenerated, 0), 0
            )}
          </p>
          <p className="text-xs text-[#63666A] mt-1">
            {clients.reduce((sum, client) => 
              sum + client.meetings.reduce((mSum, meeting) => mSum + meeting.patients, 0), 0
            )} pacientes concretados
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-[#0061A8]">
          <h4 className="text-sm font-medium text-[#63666A] mb-2">Tasa de Conversión</h4>
          <p className="text-2xl font-bold text-[#0061A8]">
            {(() => {
              const totalContacts = clients.reduce((sum, client) => 
                sum + client.meetings.reduce((mSum, meeting) => mSum + meeting.contactsGenerated, 0), 0
              );
              const totalPatients = clients.reduce((sum, client) => 
                sum + client.meetings.reduce((mSum, meeting) => mSum + meeting.patients, 0), 0
              );
              return totalContacts > 0 ? `${((totalPatients / totalContacts) * 100).toFixed(1)}%` : '0%';
            })()}
          </p>
          <p className="text-xs text-[#63666A] mt-1">
            Promedio global de conversión
          </p>
        </div>
      </div>

      {/* Filtros del Dashboard */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-[#0061A8]">Filtros del Dashboard</h3>
          <button
            onClick={handleClearFilters}
            className="text-sm text-[#63666A] hover:text-[#0061A8] flex items-center"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
            Limpiar Filtros
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#63666A] mb-1">Especialidad</label>
            <select
              name="specialty"
              value={filters.specialty}
              onChange={handleChange}
              className="w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
            >
              {allSpecialties.map(spec => (
                <option key={spec} value={spec}>{spec === '' ? 'Todas' : spec}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#63666A] mb-1">Célula</label>
            <select
              name="cell"
              value={filters.cell}
              onChange={handleChange}
              className="w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
            >
              {allCells.map(cell => (
                <option key={cell} value={cell}>{cell === '' ? 'Todas' : cell}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#63666A] mb-1">País</label>
            <select
              name="country"
              value={filters.country}
              onChange={handleChange}
              className="w-full border border-[#D1D3D4] rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#0061A8] focus:border-[#0061A8]"
            >
              {allCountries.map(country => (
                <option key={country} value={country}>{country === '' ? 'Todos' : country}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Gráficas de distribución global */}
      <GlobalClientCharts clients={filteredClients} />

      {/* Gráficas de rendimiento de clientes */}
      <ClientPerformanceOverview clients={filteredClients} />

    </div>
  );
};

export default DashboardSection;
